import { debounce } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import { StarFour } from 'phosphor-react';
import { Button, Text } from '../shared-components';
import { colors } from '../shared-components/styles';
import { useDeviceType } from './hooks/useDeviceType';
import { AssistantChatWindow } from './AssistantChatWindow';
import { useFeatureFlagPayload } from 'posthog-js/react';

export const AssistantToggle = ({ navOpen, navRef, footerRef, show = true }) => {
  // State to manage the assistant's open/close status.
  const [isOpen, setIsOpen] = useState(false);

  // Ref to the assistant element.
  const assistantRef = useRef();

  // Custom hook to determine if the device is desktop or not.
  const { isDesktop } = useDeviceType();

  // TODO: Remove ff once we fully send the ev-nav-changes flag
  const evNavChangesFF = useFeatureFlagPayload('ev-nav-changes');
  const bottom = evNavChangesFF?.value ? (isDesktop ? 32 : '24px') : 16; // Bottom offset for the assistant button.
  const right = useMemo(() => (evNavChangesFF?.value ? (isDesktop ? 24 : 32) : (isDesktop ? 32 : 16)), [isDesktop, evNavChangesFF]); // Right offset for the assistant button.

  // Dynamically set the position of the assistant based on the footer.
  useEffect(() => {
    if (!footerRef.current) return;

    const updatePosition = () => {
      const footerRect = footerRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;

      assistantRef.current.style.bottom =
        footerRect.top < viewportHeight ? `${viewportHeight - footerRect.top + bottom}px` : `${bottom}px`;
    };

    const handleEvents = debounce(() => updatePosition(), 100);

    const observer = new ResizeObserver(handleEvents);
    observer.observe(footerRef.current);

    window.addEventListener('scroll', handleEvents);
    window.addEventListener('resize', handleEvents);

    updatePosition();

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', handleEvents);
      window.removeEventListener('resize', handleEvents);
    };
  }, [footerRef]);

  // Dynamically set the position of the assistant based on the navigation bar.
  useEffect(() => {
    const updatePosition = () => {
      if (navRef.current) {
        const navRect = navRef.current.getBoundingClientRect();
        assistantRef.current.style.right = navRect.width ? `${navRect.width + right}px` : `${right}px`;
      } else {
        assistantRef.current.style.right = `${right}px`;
      }
    };

    const handleEvents = debounce(() => updatePosition(), 100);

    window.addEventListener('resize', handleEvents);

    updatePosition();

    return () => {
      window.removeEventListener('resize', handleEvents);
    };
  }, [navOpen, navRef, right]);

  return (
    <div
      ref={assistantRef}
      style={{
        position: 'fixed',
        right,
        bottom,
        transition: 'transform 0.3s ease, opacity 0.3s ease',
        transform: show ? 'translateY(0)' : 'translateY(100px)',
        zIndex: 10000,
        opacity: show ? 1 : 0,
        pointerEvents: show ? 'auto' : 'none',
      }}
    >
      {isOpen ? (
        <AssistantChatWindow close={() => setIsOpen(false)} />
      ) : (
        <Button
          secondary
          css={`
            display: inline-flex;
            justify-content: center;
            align-items: center;
            height: 64px;
            padding: 10px 20px;
            gap: 8px;
            border-radius: 48px;
          `}
          onClick={() => setIsOpen(true)}
        >
          <StarFour color={colors.purple} weight="fill" size={24} />
          {isDesktop && (
            <Text
              bold
              color={colors.black}
              css={`
                text-transform: none;
              `}
            >
              Have a question?
            </Text>
          )}
        </Button>
      )}
    </div>
  );
};
